const SH = 'text-2xl font-extrabold text-slate-900 mt-12 pt-8 border-t border-slate-200';

export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-12 prose prose-slate">
      <h1>Terms &amp; Conditions</h1>
      <p className="text-sm text-slate-500 italic">
        Applies to ADE Incorporated Websites, Software Tutorials, and Shared LearnableSkill Platform
      </p>
      <p className="text-sm text-slate-500">Effective Date: January 1, 2026</p>

      <p>
        These Terms and Conditions (&ldquo;Terms&rdquo;) govern your access to and use of websites,
        platforms, and services operated or managed by ADE Incorporated (&ldquo;ADE,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us,&rdquo; or &ldquo;our&rdquo;), including Learnable, LearnableSkill, learnableskill.com and
        learn.adeincorp.com, and any related subdomains or learning platforms.
      </p>
      <p>
        By accessing or using this Website, you agree to be bound by these Terms. If you do not agree,
        you must discontinue use immediately.
      </p>

      <h2 className={SH}>1. Organizational Structure and Scope</h2>
      <p>
        Learnable / LearnableSkill operates as a multi-credentialing Continuing Education (CE) provider
        and is a division of ADE Incorporated. While Learnable maintains responsibility for CE program
        development, credentialing compliance, instructional integrity, and participant evaluation, ADE
        Incorporated provides administrative, financial, technical, and record-keeping support for the
        Learnable platform.
      </p>
      <p>
        This organizational structure does not influence educational content, presenter independence,
        learning objectives, or credentialing outcomes.
      </p>

      <h2 className={SH}>2. Educational Purpose and Scope</h2>
      <p>Learnable CE Programs are designed to:</p>
      <ul>
        <li>Enhance professional competence</li>
        <li>Reflect current research, evidence-based practice and accepted professional standards</li>
        <li>Meet the requirements of national and state credentialing boards</li>
      </ul>
      <p>
        Course content is educational in nature only and does not constitute legal, clinical, medical,
        supervisory, or professional advice. Participants are responsible for applying course material
        in accordance with their professional scope of practice and applicable laws.
      </p>

      <h2 className={SH}>3. Separation of CE Programs and ADE Software Tutorials</h2>
      <p>
        Learnable CE programs are distinct and separate from ADE software tutorials that may be hosted
        on the same platform.
      </p>
      <ul>
        <li>ADE software tutorials are instructional resources focused on the use of ADE software applications</li>
        <li>Tutorials are free of charge</li>
        <li>Tutorials are not eligible for Continuing Education (CE) credit</li>
        <li>Tutorials do not influence, replace, or supplement CE instructional content</li>
        <li>
          Participation in ADE tutorials does not confer CE credit and is not required for participation
          in Learnable CE programs
        </li>
      </ul>

      <h2 className={SH}>4. Presenter Qualifications</h2>
      <p>
        Presenters and instructors are selected based on their education, licensure or certification,
        training, and professional experience relevant to the subject matter. Presenters are responsible
        for the accuracy, integrity, and ethical delivery of course content.
      </p>

      <h2 className={SH}>5. Conflict of Interest, Commercial Support, and Advertising</h2>
      <p>
        All presenters, planners, and instructors disclose any financial or professional relationships
        that could reasonably be perceived as a conflict of interest. These disclosures are made at
        the beginning of each continuing education (CE) activity.
      </p>

      <h2 className={SH}>6. Advertising and Commercial Influence Policy</h2>
      <ul>
        <li>
          Learnable CE programs are developed and delivered to ensure instructional content is free
          from commercial bias
        </li>
        <li>
          Advertising, product promotion, or marketing is not permitted within CE instructional content,
          learning objectives, or educational materials
        </li>
        <li>
          References to ADE Incorporated or its software products, if any, are presented outside of CE
          instructional time and do not influence course content, presenter selection, or educational outcomes
        </li>
      </ul>

      <h2 className={SH}>7. Optional Communications</h2>
      <p>Participants may voluntarily opt into newsletters or communications that may include:</p>
      <ul>
        <li>Information about upcoming Learnable CE programs</li>
        <li>Educational announcements</li>
        <li>Updates related to ADE software applications or tutorials</li>
      </ul>
      <p>
        Participation in Learnable CE programs is not contingent upon receiving marketing or promotional
        communications, and participants may opt out of non-essential communications at any time.
      </p>

      <h2 className={SH}>8. Attendance, Participation, and Credit Awarding</h2>
      <p>
        Participants must meet all attendance and participation requirements to receive CE credit.
      </p>
      <ul>
        <li>Full attendance is required for live programs</li>
        <li>All instructional components must be completed for self-paced or asynchronous programs</li>
        <li>CE credit is awarded for instructional time only</li>
        <li>Registration time, breaks, and non-instructional activities are excluded</li>
        <li>Partial credit is not awarded</li>
        <li>Participants arriving late or leaving early are not eligible for CE credit</li>
        <li>Completion of a course evaluation is required to receive CE credit</li>
      </ul>

      <h2 className={SH}>9. Verification of Attendance, Certificates, and Recordkeeping</h2>
      <p>
        Attendance is verified using appropriate methods, including electronic tracking or participation
        monitoring. Certificates of completion are issued upon successful completion of all course
        requirements and include:
      </p>
      <ul>
        <li>Course title</li>
        <li>Date(s) of instruction</li>
        <li>Number of approved clock hours</li>
        <li>Delivery format (synchronous or asynchronous)</li>
        <li>Provider information</li>
      </ul>
      <p>
        Recordkeeping of Learnable CE records — including attendance documentation, certificates,
        refund requests, and complaints — are retained for a minimum of five (5) years. All CE records
        are securely maintained by ADE Incorporated on behalf of Learnable, with access limited to
        authorized personnel for:
      </p>
      <ul>
        <li>CE administration</li>
        <li>Credentialing audits</li>
        <li>Required regulatory or credentialing reporting</li>
      </ul>
      <p>
        Participants are responsible for retaining their own certificates and verifying acceptance of
        CE credit with their licensing or certifying board.
      </p>

      <h2 className={SH}>10. Credentialing Body Approval and Participant Responsibility</h2>
      <p>Each Learnable CE course clearly identifies:</p>
      <ul>
        <li>Whether CE credit is available</li>
        <li>Approved credentialing bodies</li>
        <li>Number of approved clock hours</li>
        <li>Delivery format</li>
      </ul>
      <p>
        Approval information is accurate at the time of publication and applies only to the specific
        course and format identified. Participants are responsible for confirming acceptance of CE
        credit with their individual licensing or certifying body.
      </p>

      <h2 className={SH}>11. Fees, Payment Processing, and Refunds</h2>
      <p>All course fees and refund policies are clearly disclosed prior to registration.</p>

      <h3 className="text-lg font-bold text-slate-800 mt-8">Payment Processing</h3>
      <ul>
        <li>All Learnable course fees are processed exclusively through ADE Incorporated</li>
        <li>ADE Incorporated is the merchant of record for all financial transactions</li>
        <li>Secure, industry-standard payment processors are used</li>
        <li>
          Credit card information is not stored beyond what is necessary for lawful accounting and auditing
        </li>
      </ul>

      <h3 className="text-lg font-bold text-slate-800 mt-8">Refund Policy</h3>
      <ul>
        <li>
          Written cancellation must be received at least 48 hours prior to the scheduled program start
          time to be eligible for a refund
        </li>
        <li>
          Cancellations received fewer than 48 hours prior are not eligible for a refund but may, at
          Learnable&rsquo;s discretion, receive a credit toward a future course
        </li>
        <li>If Learnable cancels a course, participants receive a full refund or transfer option</li>
        <li>
          Approved refunds are processed within 14 business days and documented with course records
        </li>
      </ul>

      <h2 className={SH}>12. Participant Complaints and Grievances</h2>
      <p>Learnable maintains a formal complaint and grievance process.</p>
      <ul>
        <li>Complaints must be submitted in writing within 10 calendar days of course completion</li>
        <li>Complaints are reviewed within 10 business days</li>
        <li>Written responses are provided within 30 calendar days</li>
        <li>All complaints and resolutions are documented and retained</li>
      </ul>
      <p>
        If a complaint relates to NBCC Continuing Education Provider Standards, Learnable will notify
        NBCC within 60 days of becoming aware of the issue.
      </p>

      <h2 className={SH}>13. Privacy, Confidentiality, and Data Security</h2>
      <p>Participant data is treated as confidential and used only for:</p>
      <ul>
        <li>Course administration</li>
        <li>Payment processing</li>
        <li>Credentialing documentation</li>
        <li>Required audits and reporting</li>
      </ul>
      <p>
        ADE Incorporated maintains administrative, technical, and physical safeguards aligned with
        HIPAA and HITECH security principles, where applicable, to protect participant information.
        Personal information is not sold or shared for marketing purposes without explicit consent.
      </p>

      <h2 className={SH}>14. Communications and Optional Subscriptions</h2>
      <p>
        Participants who opt into newsletters, blogs, or platform communications acknowledge that:
      </p>
      <ul>
        <li>
          Communications may include informational posts from ADE tutorials and/or Learnable CE programs
        </li>
        <li>CE participation remains free from advertising and promotional influence</li>
        <li>Participants may unsubscribe from non-essential communications at any time</li>
      </ul>

      <h2 className={SH}>15. Modifications to Terms</h2>
      <p>
        Learnable and ADE Incorporated reserve the right to update these Terms as required by law,
        credentialing standards, or operational needs. Continued participation constitutes acceptance
        of revised Terms.
      </p>

      <h2 className={SH}>16. Governing Law</h2>
      <p>
        These Terms are governed by the State of Michigan and federal law without regard to
        conflict-of-law principles.
      </p>
    </div>
  );
}
